import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Loader2, Mail, Paperclip, ExternalLink, X } from 'lucide-react';
import api from '../lib/api';
import { formatDateTime } from '../lib/utils';

/**
 * Pop-out dialog for reading a full email from the customer timeline.
 *
 * Two sources are supported:
 *   - source: 'ms365'        — synced inbound/outbound from a Helios mailbox
 *     requires: ms365_id, mailbox
 *   - source: 'email_log'    — outbound email sent from the CRM
 *     requires: customer_id, email_log_id
 *
 * Body HTML is sandboxed in a same-origin iframe so external tracking pixels
 * / scripts in inbound mail can't touch the parent page.
 */
export const EmailViewDialog = ({ open, onOpenChange, source, params }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      setEmail(null);
      try {
        let res;
        if (source === 'ms365') {
          res = await api.get(`/email-timeline/email/${encodeURIComponent(params.ms365_id)}/body`, {
            params: { mailbox: params.mailbox },
          });
        } else if (source === 'email_log') {
          res = await api.get(`/customers/${params.customer_id}/email-log/${params.email_log_id}`);
        } else {
          throw new Error('Unsupported email source');
        }
        if (!cancelled) setEmail(res.data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || 'Unable to load email');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [open, source, params?.ms365_id, params?.mailbox, params?.customer_id, params?.email_log_id]);

  const renderBody = () => {
    if (!email) return null;
    const isHtml = (email.content_type || '').includes('html');
    const html = email.body || '';

    if (isHtml) {
      // srcDoc sandbox keeps external resources from running in our origin
      return (
        <iframe
          title="Email body"
          srcDoc={html}
          sandbox=""
          className="w-full h-[55vh] bg-white border-0 rounded"
          data-testid="email-body-iframe"
        />
      );
    }
    return (
      <pre className="whitespace-pre-wrap text-sm text-slate-700 bg-slate-50 p-4 rounded border border-slate-200 max-h-[55vh] overflow-auto">
        {html || '(empty)'}
      </pre>
    );
  };

  const direction = email?.direction || (source === 'email_log' ? 'sent' : '');
  const subject = email?.subject || params?.subject || '';
  const fromAddr = email?.from_address || email?.from || '';
  const fromName = email?.from_name || '';
  const toAddrs = email?.to_addresses || (email?.to ? [email.to] : []);
  const ccAddrs = email?.cc_addresses || email?.cc || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[90vw] w-[90vw] sm:max-w-3xl p-0 gap-0"
        data-testid="email-view-dialog"
      >
        <DialogHeader className="px-5 py-3 border-b border-slate-200">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <Mail className="w-4 h-4 text-slate-500 flex-shrink-0" />
              <DialogTitle className="text-base truncate">
                {subject || 'Email'}
              </DialogTitle>
              {direction && (
                <Badge
                  className={
                    direction === 'inbound'
                      ? 'bg-sky-100 text-sky-700 border-sky-200 text-[10px]'
                      : 'bg-violet-100 text-violet-700 border-violet-200 text-[10px]'
                  }
                >
                  {direction === 'inbound' ? 'Received' : 'Sent'}
                </Badge>
              )}
            </div>
          </div>
          <DialogDescription className="sr-only">
            Full email content from the customer timeline.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-16 text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            <span className="text-sm">Loading full email…</span>
          </div>
        )}

        {!loading && error && (
          <div className="px-5 py-10 text-center text-sm text-slate-500" data-testid="email-view-error">
            <p className="font-medium text-slate-700 mb-1">Can&apos;t load this email</p>
            <p>{error}</p>
          </div>
        )}

        {!loading && !error && email && (
          <div className="px-5 py-3 space-y-3 max-h-[80vh] overflow-auto">
            <div className="text-xs text-slate-600 space-y-1 bg-slate-50 rounded border border-slate-200 px-3 py-2">
              {(fromAddr || fromName) && (
                <div><span className="font-medium text-slate-500 w-12 inline-block">From:</span> {fromName ? `${fromName} <${fromAddr}>` : fromAddr}</div>
              )}
              {toAddrs && toAddrs.length > 0 && (
                <div><span className="font-medium text-slate-500 w-12 inline-block">To:</span> {Array.isArray(toAddrs) ? toAddrs.join(', ') : toAddrs}</div>
              )}
              {ccAddrs && (Array.isArray(ccAddrs) ? ccAddrs.length > 0 : ccAddrs) && (
                <div><span className="font-medium text-slate-500 w-12 inline-block">Cc:</span> {Array.isArray(ccAddrs) ? ccAddrs.join(', ') : ccAddrs}</div>
              )}
              {(email.received_at || email.sent_at) && (
                <div><span className="font-medium text-slate-500 w-12 inline-block">Date:</span> {formatDateTime(email.received_at || email.sent_at)}</div>
              )}
              {email.mailbox && (
                <div><span className="font-medium text-slate-500 w-12 inline-block">Mailbox:</span> {email.mailbox}</div>
              )}
              {email.has_attachments && (
                <div className="flex items-center gap-1 text-amber-700">
                  <Paperclip className="w-3 h-3" />
                  <span>Has attachments</span>
                </div>
              )}
              {email.attachment_names && email.attachment_names.length > 0 && (
                <div className="flex items-center gap-1 text-amber-700">
                  <Paperclip className="w-3 h-3" />
                  <span>{email.attachment_names.join(', ')}</span>
                </div>
              )}
            </div>

            {renderBody()}
          </div>
        )}

        <DialogFooter className="px-5 py-3 border-t border-slate-200">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            data-testid="email-view-close"
          >
            <X className="w-3.5 h-3.5 mr-1" />
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EmailViewDialog;
